import { useState, useCallback } from 'react'
import { Eye, ChevronDown, ChevronRight, Settings } from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Checkbox } from '@/components/ui/checkbox'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { ApplyTypeBadge, RefundStatusBadge } from '../../components/RefundStatusBadge'
import { REFUND_STATUS, type Refund } from '@/schemas/supplier'

interface RefundTableProps {
  refunds: Refund[]
  isLoading?: boolean
  onViewDetail?: (refund: Refund) => void
  onProcess?: (refund: Refund) => void
}

// 商品汇总组件
function CartSummary({ totalQuantity, cartTotal, refundPrice }: {
  totalQuantity: number
  cartTotal: number
  refundPrice: number
}) {
  return (
    <div className="flex justify-end items-center gap-6 mt-4 pt-4 border-t">
      <div className="text-sm text-muted-foreground">
        共 <span className="font-medium text-foreground">{totalQuantity}</span> 件商品
      </div>
      <div className="text-sm text-muted-foreground">
        商品总价：<span className="font-medium text-foreground">¥{cartTotal.toFixed(2)}</span>
      </div>
      <div className="text-sm">
        退款金额：<span className="font-semibold text-lg text-red-500">¥{refundPrice.toFixed(2)}</span>
      </div>
    </div>
  )
}

export function RefundTable({ refunds, isLoading, onViewDetail, onProcess }: RefundTableProps) {
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set())
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set())

  const allSelected = refunds.length > 0 && selectedRows.size === refunds.length
  const someSelected = selectedRows.size > 0 && !allSelected

  const handleSelectAll = () => {
    if (allSelected) {
      setSelectedRows(new Set())
    } else {
      setSelectedRows(new Set(refunds.map((refund) => refund.id)))
    }
  }

  const handleSelectRow = (id: number) => {
    const newSelected = new Set(selectedRows)
    if (newSelected.has(id)) {
      newSelected.delete(id)
    } else {
      newSelected.add(id)
    }
    setSelectedRows(newSelected)
  }

  const toggleExpand = (id: number) => {
    const newExpanded = new Set(expandedRows)
    if (newExpanded.has(id)) {
      newExpanded.delete(id)
    } else {
      newExpanded.add(id)
    }
    setExpandedRows(newExpanded)
  }

  const handleViewDetail = (refund: Refund) => {
    onViewDetail?.(refund)
  }

  // 计算商品汇总
  const calculateCartTotal = useCallback((cartInfo: Refund['cart_info']) => {
    if (!cartInfo || cartInfo.length === 0) return 0
    return cartInfo.reduce((sum, item) => sum + (item.price ?? 0) * (item.cart_num ?? 0), 0)
  }, [])

  if (isLoading) {
    return (
      <div className="rounded-xl border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12"><Skeleton className="h-4 w-4" /></TableHead>
              <TableHead className="w-8"></TableHead>
              <TableHead className="w-36"><Skeleton className="h-4 w-20" /></TableHead>
              <TableHead><Skeleton className="h-4 w-24" /></TableHead>
              <TableHead className="text-right"><Skeleton className="h-4 w-16 ml-auto" /></TableHead>
              <TableHead className="w-24"><Skeleton className="h-4 w-16" /></TableHead>
              <TableHead className="w-24"><Skeleton className="h-4 w-16" /></TableHead>
              <TableHead className="w-40"><Skeleton className="h-4 w-32" /></TableHead>
              <TableHead className="w-32 text-center"><Skeleton className="h-4 w-20 mx-auto" /></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {Array.from({ length: 5 }).map((_, i) => (
              <TableRow key={i}>
                <TableCell><Skeleton className="h-4 w-4" /></TableCell>
                <TableCell><Skeleton className="h-4 w-4" /></TableCell>
                <TableCell><Skeleton className="h-4 w-28" /></TableCell>
                <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                <TableCell><Skeleton className="h-4 w-16 ml-auto" /></TableCell>
                <TableCell><Skeleton className="h-6 w-16" /></TableCell>
                <TableCell><Skeleton className="h-6 w-16" /></TableCell>
                <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                <TableCell><Skeleton className="h-8 w-20 mx-auto" /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    )
  }

  if (refunds.length === 0) {
    return (
      <div className="rounded-xl border bg-card py-12 text-center">
        <p className="text-muted-foreground">暂无退款数据</p>
      </div>
    )
  }

  return (
    <div className="rounded-xl border bg-card">
      {/* 批量操作栏 */}
      {selectedRows.size > 0 && (
        <div className="flex items-center justify-between border-b bg-muted/50 px-4 py-3">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">
              已选择 <span className="font-medium text-foreground">{selectedRows.size}</span> 项
            </span>
          </div>
        </div>
      )}

      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className="w-12">
              <Checkbox
                checked={allSelected}
                ref={(ref) => {
                  if (ref) {
                    (ref as HTMLButtonElement).dataset.state = someSelected
                      ? 'indeterminate'
                      : allSelected
                      ? 'checked'
                      : 'unchecked'
                  }
                }}
                onCheckedChange={handleSelectAll}
                aria-label="全选"
              />
            </TableHead>
            <TableHead className="w-8"></TableHead>
            <TableHead className="w-36">订单号</TableHead>
            <TableHead>商品</TableHead>
            <TableHead className="text-right">退款金额</TableHead>
            <TableHead className="w-24">申请类型</TableHead>
            <TableHead className="w-24">状态</TableHead>
            <TableHead className="w-48">退款信息</TableHead>
            <TableHead className="w-40">申请时间</TableHead>
            <TableHead className="w-32 text-center">操作</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {refunds.map((refund) => {
            const isExpanded = expandedRows.has(refund.id)
            const cartInfo = refund.cart_info || []
            const cartTotal = calculateCartTotal(cartInfo)
            const canProcess = refund.refund_type !== REFUND_STATUS.REFUNDED && refund.refund_type !== REFUND_STATUS.REJECTED

            return (
              <>
                {/* 主行 */}
                <TableRow
                  key={refund.id}
                  data-selected={selectedRows.has(refund.id)}
                  className="group"
                >
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={selectedRows.has(refund.id)}
                      onCheckedChange={() => handleSelectRow(refund.id)}
                      aria-label={`选择退款 ${refund.id}`}
                    />
                  </TableCell>
                  <TableCell>
                    <button
                      type="button"
                      onClick={() => toggleExpand(refund.id)}
                      className="flex items-center justify-center w-6 h-6 rounded hover:bg-muted transition-colors"
                      aria-label={isExpanded ? '收起' : '展开'}
                    >
                      {isExpanded ? (
                        <ChevronDown className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      )}
                    </button>
                  </TableCell>
                  <TableCell>
                    <span className="font-mono text-sm">{refund.order_id}</span>
                  </TableCell>
                  <TableCell>
                    <div className="max-w-[200px]">
                      {cartInfo.length > 0 && (
                        <>
                          <p className="text-sm truncate">{cartInfo[0].product_name}</p>
                          {cartInfo.length > 1 && (
                            <p className="text-xs text-muted-foreground">
                              +{cartInfo.length - 1} 件商品
                            </p>
                          )}
                        </>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <span className="font-semibold text-red-500">
                      -{refund.refund_price.toFixed(2)}
                    </span>
                  </TableCell>
                  <TableCell>
                    <ApplyTypeBadge applyType={refund.apply_type} />
                  </TableCell>
                  <TableCell>
                    <RefundStatusBadge refundStatus={refund.refund_type} />
                  </TableCell>
                  <TableCell>
                    <div className="max-w-[180px]">
                      {refund.refund_reason && (
                        <p className="text-sm truncate" title={refund.refund_reason}>
                          {refund.refund_reason}
                        </p>
                      )}
                      {refund.refund_explain && (
                        <p className="text-xs text-muted-foreground truncate" title={refund.refund_explain}>
                          {refund.refund_explain}
                        </p>
                      )}
                      {!refund.refund_reason && !refund.refund_explain && (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-muted-foreground">{refund.add_time}</span>
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleViewDetail(refund)}
                      >
                        <Eye className="mr-1.5 h-3.5 w-3.5" />
                        详情
                      </Button>
                      {canProcess && onProcess && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onProcess(refund)}
                        >
                          <Settings className="mr-1.5 h-3.5 w-3.5" />
                          处理
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>

                {/* 展开行 - 商品详情 */}
                {isExpanded && (
                  <TableRow
                    key={`${refund.id}-expanded`}
                    className="bg-muted/30 hover:bg-muted/40"
                  >
                    <TableCell colSpan={10} className="p-0">
                      <div className="px-12 py-4">
                        {/* 商品列表 */}
                        <div className="space-y-3">
                          {cartInfo.length > 0 ? (
                            cartInfo.map((item, idx) => (
                              <div
                                key={item.cart_id || idx}
                                className="flex items-center gap-4 p-3 bg-background rounded-lg border"
                              >
                                {/* 商品图片 */}
                                <div className="w-16 h-16 rounded-md overflow-hidden bg-muted flex-shrink-0">
                                  {item.product_image ? (
                                    <img
                                      src={item.product_image}
                                      alt={item.product_name}
                                      className="w-full h-full object-cover"
                                    />
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center text-muted-foreground text-xs">
                                      暂无图片
                                    </div>
                                  )}
                                </div>

                                {/* 商品信息 */}
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium truncate">{item.product_name}</p>
                                  {item.product_attr && (
                                    <p className="text-xs text-muted-foreground mt-0.5">
                                      规格：{item.product_attr}
                                    </p>
                                  )}
                                  {item.sku && (
                                    <p className="text-xs text-muted-foreground">
                                      SKU：{item.sku}
                                    </p>
                                  )}
                                </div>

                                {/* 单价 */}
                                <div className="text-right w-24">
                                  <p className="text-sm text-muted-foreground">单价</p>
                                  <p className="text-sm font-medium">¥{(item.price ?? 0).toFixed(2)}</p>
                                </div>

                                {/* 数量 */}
                                <div className="text-right w-16">
                                  <p className="text-sm text-muted-foreground">数量</p>
                                  <p className="text-sm font-medium">x{item.cart_num ?? 0}</p>
                                </div>

                                {/* 小计 */}
                                <div className="text-right w-28">
                                  <p className="text-sm text-muted-foreground">小计</p>
                                  <p className="text-sm font-semibold">
                                    ¥{((item.price ?? 0) * (item.cart_num ?? 0)).toFixed(2)}
                                  </p>
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="text-center py-4 text-muted-foreground text-sm">
                              暂无商品信息
                            </div>
                          )}
                        </div>

                        {/* 底部汇总 */}
                        {cartInfo.length > 0 && (() => {
                          const totalQuantity = cartInfo.reduce((sum, item) => sum + (item.cart_num ?? 0), 0)
                          return (
                            <CartSummary
                              totalQuantity={totalQuantity}
                              cartTotal={cartTotal}
                              refundPrice={refund.refund_price}
                            />
                          )
                        })()}
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </>
            )
          })}
        </TableBody>
      </Table>
    </div>
  )
}
